import { Component, Inject, Input, OnDestroy } from '@angular/core';

import { DialogManagerService } from '@src/services';
import { TranslatePipe } from '@app/shared/pipes/translate.pipe';
import { Store } from '@src/store';
import { consts } from '@src/constants/consts';
import { tagsActions } from '@src/store/reducers/tagTypesReducer';
import { OBJECT_TYPES } from '@src/constants/objectTypes';
import { AddTagDialogData } from '@app/shared/components/dialogs';

import * as _ from 'lodash';
import { packagesActions } from '@src/store/reducers/packagesReducer';

const PACKAGE_OBJECT_TYPE = 'package';
const PAGE_SIZE = 10;

@Component({
    selector: 'package-tags',
    template: require('./package-tags.component.html'),
    styles: [require('./package-tags.component.scss')]
})
export class PackageTagsComponent implements OnDestroy {
    @Input() public selectedPackage: any;
    @Input() public projectId: any;
    @Input() public tagTypes: any;

    public packageTagsList: any[];

    private mappingData: any[];
    private selectedPackageId: any;
    private onScriptEdit: any;
    private onSelectedPackageEvent: any;

    constructor(
        private translatePipe: TranslatePipe,
        private store: Store,
        @Inject('$scope') private $scope: any,
        @Inject('authorizationService') private authorizationService: any,
        private dialogManagerService: DialogManagerService) {
        this.mappingData = [];
        this.onSelectedPackageEvent = $scope.$on(
            consts.emitedEventsTypes.selectedPackageEvent,
            (__: any, data: any) => {
                this.selectedPackageId = data.packageId;
                this.packageTagsList = [];
                this.getPackageTags(this.selectedPackageId);
                this.getTagTypes();
            }
        );
        this.onScriptEdit = $scope.$on(consts.emitedEventsTypes.scriptEdit, (__: any, data: any) => {
            this.selectedPackageId = data.packageId;
            this.getPackageTags(this.selectedPackageId);
        });
    }

    public ngOnDestroy() {
        this.onSelectedPackageEvent();
        this.onScriptEdit();
    }

    private async getPackageTags(packageId: any) {
        const response = await this.store.dispatch({
            type: tagsActions.GET_OBJECT_TAGS,
            payload: {
                objectTypeId: OBJECT_TYPES[PACKAGE_OBJECT_TYPE],
                objectId: packageId,
                projectId: this.projectId
            }
        });
        this.getAssignedTags(response);
    }

    private async getTagTypes() {
        const response = await this.store.dispatch({
            type: tagsActions.GET_TAG_TYPES_LIST,
            payload: { pageNumber: 1, pageSize: PAGE_SIZE }
        });
        this.tagTypes = response;
    }

    public onRemovePackageTag(tag: any) {
        const onConfirm = () => {
            let objId = '';
            let objTypeId = '';
            const tagInfoObj = this.mappingData.find(x => x.tagTypeId === tag.tagTypeId && x.tag.name === tag.name);
            if (tagInfoObj && tagInfoObj.tag.inheritedFromObject) {
                objId = tagInfoObj.tag.inheritedFromObject.objectId;
                objTypeId = tagInfoObj.tag.inheritedFromObject.objectTypeId;
            }
            this.store.dispatch({
                type: tagsActions.REMOVE_TAG_FROM_OBJECT,
                payload: {
                    projectId: this.projectId,
                    objectTypeId: objTypeId || tagInfoObj.tag.mappingObject.objectTypeId,
                    objectId: objId || this.selectedPackageId,
                    tagName: tag.name,
                    tagTypeId: tag.tagTypeId
                }
            }).then((res: any) => {
                if (res) {
                    this.packageTagsList = this.packageTagsList.filter(pkgTag =>
                        pkgTag.name !== tag.name || pkgTag.tagTypeId !== tag.tagTypeId);
                    this.store.dispatch({ type: packagesActions.GET_PACKAGES, payload: { id: this.projectId } });
                }
            });
        };
        const dialogData = { title: 'Delete Tag', action: 'delete', objectType: 'tag', item: tag.name };
        this.dialogManagerService.showConfirmationDialog(dialogData, onConfirm);
    }

    public getTagTypeName(tagTypeId: any) {
        if (this.tagTypes && this.tagTypes.length > 0) {
            return this.tagTypes.find((tagType: any) => tagType.id === tagTypeId).name;
        }
    }

    public async openAddTagPopup() {
        const canManagePackageTags = await this.authorizationService
            .isAuthorizedToPerformAction(consts.userPermissions.ManageTags);
        const translations = await this.translatePipe.transform(['ERROR', 'ERROR_OCCURRED', 'USER_UNAUTHORIZED',
            'ERROR_PENDING_TAGS_TO_SAVE', 'ERROR_NO_TAGS_SELECTED']);
        if (!canManagePackageTags) {
            this.$scope.$apply(() => {
                this.dialogManagerService.showErrorDialog({
                    title: translations['ERROR'],
                    text: `${translations['ERROR_OCCURRED']} : ${translations['USER_UNAUTHORIZED']}`
                });
            });
            return;
        }

        const dialogData: AddTagDialogData = {
            store: this.store,
            showErrorDialog: (data: any) => this.dialogManagerService.showErrorDialog(data),
            packageTags: [],
            addedTags: [...this.packageTagsList]
        };
        const onConfirm = (data: AddTagDialogData) => {
            const packageTags = data.packageTags;
            const tagsToAssign = [];
            if (!packageTags || _.isEmpty(packageTags)) {
                this.dialogManagerService.showErrorDialog({ title: '', text: translations['ERROR_NO_TAGS_SELECTED'] });
            } else {
                for (const tagTypeId in packageTags) {
                    const tagTypeTags = packageTags[tagTypeId];
                    for (const pckgTag of tagTypeTags.tagsList) {
                        const tag = { ...pckgTag, ...{ tagTypeName: tagTypeTags.tagType.name } };
                        tagsToAssign.push(tag);
                    }
                }
                this.assignTagsToObject(tagsToAssign);
            }
        };
        this.dialogManagerService.showAddTagDialog(dialogData, onConfirm);
    }

    private getAssignedTags(response: any) {
        const tags = [];
        if (response && response.length > 0) {
            for (const tag of response) {
                tags.push(tag.assignedTag);
                this.mappingData.push({
                    tagTypeId: tag.assignedTag.tagTypeId, tag: {
                        name: tag.assignedTag.name,
                        mappingObject: tag.mappingObject,
                        inheritedFromObject: tag.inheritedFromObject
                    }
                });
            }
            this.packageTagsList = _.uniqBy(tags, (tag: any) => [tag.name, tag.tagTypeId].join());
        }
    }

    private assignTagsToObject(tags: any) {
        if (tags && tags.length > 0) {
            this.store.dispatch({
                type: tagsActions.ADD_TAG_TO_OBJECT,
                payload: {
                    tags,
                    projectId: this.projectId,
                    objectTypeId: OBJECT_TYPES[PACKAGE_OBJECT_TYPE],
                    objectId: this.selectedPackageId
                }
            }).then(() => {
                this.store.dispatch({ type: packagesActions.GET_PACKAGES, payload: { id: this.projectId } });
                this.getPackageTags(this.selectedPackageId);
            });
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/authorized/project/packageManager/packages/packages-list/package-tags/package-tags.component.ts